import { GameEngine } from "~/engine/instance";
import { TERRAIN_TYPES, VOXEL_EMPTY } from "~/terrain/types";
import type { VoxelChunk } from "~/terrain/voxel-terrain";
import { getRandomBinary } from "~/utils/random";

/**
 * Sand Simulation with Density-Based Physics
 *
 * This system implements a density-based particle simulation where:
 * - Materials have density values (water = 1.0, air = 0.0, solids > 1.0)
 * - Denser materials can push through less dense materials
 * - Lighter materials exhibit buoyancy and float upward through heavier materials
 * - All materials fall due to gravity, with heavier materials falling faster
 * - Smoke has decay behavior: when it can't move, it accumulates "tiredness"
 *   and disappears after reaching MAX_TIREDNESS
 *
 * Movement priorities:
 * 1. Gravity (downward movement, scaled by density)
 * 2. Diagonal movement (for flowing materials)
 * 3. Buoyancy (upward movement for light materials)
 * 4. Horizontal movement (for liquids like water)
 */

const UNDEFINED_MOVERESULT = -1;
const MOVED_MOVERESULT = -2;

type VoxelChunkPrepared = WithNonNullable<VoxelChunk, "voxelStructs">;

const GRAVITY = -0.08;

enum CAN_MOVE_THROUGH {
  CAN_MOVE,
  CANNOT_MOVE,
  DESTROY,
}

function canVoxelMoveThrough(
  movingVoxelType: number,
  obstacleVoxelType: number,
): CAN_MOVE_THROUGH {
  if (obstacleVoxelType === VOXEL_EMPTY) {
    return CAN_MOVE_THROUGH.CAN_MOVE; // Can always move through empty space
  }

  if (movingVoxelType === VOXEL_EMPTY) {
    return CAN_MOVE_THROUGH.CANNOT_MOVE; // Empty space can't move through anything
  }

  // if it's a liquid, it can move through smoke
  if (
    movingVoxelType === TERRAIN_TYPES.water &&
    obstacleVoxelType === TERRAIN_TYPES.smoke
  ) {
    return CAN_MOVE_THROUGH.CAN_MOVE;
  }

  // if it's a solid, it can move through water
  if (
    (movingVoxelType === TERRAIN_TYPES.dirt ||
      movingVoxelType === TERRAIN_TYPES.sand ||
      movingVoxelType === TERRAIN_TYPES.grass) &&
    obstacleVoxelType === TERRAIN_TYPES.water
  ) {
    return CAN_MOVE_THROUGH.CAN_MOVE;
  }

  // if it's a solid, it can destroy smoke
  if (
    (movingVoxelType === TERRAIN_TYPES.dirt ||
      movingVoxelType === TERRAIN_TYPES.sand ||
      movingVoxelType === TERRAIN_TYPES.grass) &&
    obstacleVoxelType === TERRAIN_TYPES.smoke
  ) {
    return CAN_MOVE_THROUGH.DESTROY;
  }

  return CAN_MOVE_THROUGH.CANNOT_MOVE;
}

function moveVoxel(
  initialChunk: VoxelChunkPrepared,
  chunkMap: Map<number, VoxelChunk>,
  initialIndex: number,
  deltaX: number,
  deltaY: number,
): number {
  const { chunkWidthInVoxels: chunkW, chunkHeightInVoxels: chunkH } =
    GameEngine.voxelTerrain;

  const localX = initialIndex % chunkW;
  const localY = Math.floor(initialIndex / chunkW);

  // Store the initial voxel's terrain type to avoid changing VoxelBuffer internal state
  const initialVoxelTerrainType =
    initialChunk.voxelAt(initialIndex).terrainType;

  let finalIndex = initialIndex;
  let finalChunk = initialChunk;
  let finalVoxelType = VOXEL_EMPTY;

  let shouldDestroy = false;

  const incrX = Math.sign(deltaX);
  const incrY = Math.sign(deltaY);
  outerLoop: for (let ix = Math.abs(incrX); ix <= Math.abs(deltaX); ix++) {
    for (let iy = Math.abs(incrY); iy <= Math.abs(deltaY); iy++) {
      const newLocalX = localX + ix * incrX;
      const newLocalY = localY + iy * incrY;

      let nextIndex: number;
      let nextChunk: VoxelChunk | undefined;

      if (
        // If moving within same chunk
        newLocalX >= 0 &&
        newLocalX < chunkW &&
        newLocalY >= 0 &&
        newLocalY < chunkH
      ) {
        nextIndex = newLocalY * chunkW + newLocalX;
        nextChunk = initialChunk;
      } else {
        // else, moving to neighbor chunk

        const chunkOffsetX = Math.floor(newLocalX / chunkW);
        const chunkOffsetY = Math.floor(newLocalY / chunkH);
        const neighborChunkX = initialChunk.chunkPlacement.x + chunkOffsetX;
        const neighborChunkY = initialChunk.chunkPlacement.y + chunkOffsetY;

        const neighborChunkId = GameEngine.voxelTerrain.getChunkId(
          neighborChunkX,
          neighborChunkY,
        );
        nextChunk = chunkMap.get(neighborChunkId);

        const finalLocalX = ((newLocalX % chunkW) + chunkW) % chunkW;
        const finalLocalY = ((newLocalY % chunkH) + chunkH) % chunkH;
        nextIndex = finalLocalY * chunkW + finalLocalX;
      }

      if (!nextChunk?.voxelStructs) return UNDEFINED_MOVERESULT;

      const nextVoxel = nextChunk.voxelAt(nextIndex);
      const nextVoxelType = nextVoxel.terrainType;

      finalVoxelType = nextVoxelType;

      // Check if we can move through this voxel based on density
      const canMoveThrough = canVoxelMoveThrough(
        initialVoxelTerrainType,
        nextVoxelType,
      );
      if (canMoveThrough === CAN_MOVE_THROUGH.CANNOT_MOVE) {
        break outerLoop;
      } else if (canMoveThrough === CAN_MOVE_THROUGH.DESTROY) {
        finalVoxelType = VOXEL_EMPTY;
        shouldDestroy = true;
        finalIndex = nextIndex;
        finalChunk = nextChunk as VoxelChunkPrepared;
        break outerLoop;
      }

      finalIndex = nextIndex;
      finalChunk = nextChunk as VoxelChunkPrepared;
    }
  }

  // if we're hitting an obstacle (i.e. not moving)
  if (finalIndex === initialIndex && finalChunk === initialChunk) {
    return finalVoxelType;
  }

  // Now access the initial voxel data for the move operation
  const initialVoxel = initialChunk.voxelAt(initialIndex);

  const {
    uid,
    terrainType,
    destruction,
    velocityX,
    velocityY,
    lastDestructedAt,
    tiredness,
  } = initialVoxel;

  // Check if we need to swap voxels (when moving through a less dense material)
  if (finalVoxelType !== VOXEL_EMPTY && !shouldDestroy) {
    const finalVoxel = finalChunk.voxelAt(finalIndex);
    // We're displacing another voxel - swap them
    const displacedData = {
      uid: finalVoxel.uid,
      terrainType: finalVoxel.terrainType,
      destruction: finalVoxel.destruction,
      velocityX: finalVoxel.velocityX,
      velocityY: finalVoxel.velocityY,
      lastDestructedAt: finalVoxel.lastDestructedAt,
      tiredness: finalVoxel.tiredness,
    };

    // Move our voxel to the final position
    finalVoxel.uid = uid;
    finalVoxel.terrainType = terrainType;
    finalVoxel.destruction = destruction;
    finalVoxel.velocityX = velocityX;
    finalVoxel.velocityY = velocityY;
    finalVoxel.lastDestructedAt = lastDestructedAt;
    finalVoxel.tiredness = tiredness;
    finalVoxel.modified = true;
    finalChunk.needsRenderRect.addPointByIndex(finalIndex);

    // Move the displaced voxel to our initial position
    initialVoxel.at(initialIndex);
    initialVoxel.uid = displacedData.uid;
    initialVoxel.terrainType = displacedData.terrainType;
    initialVoxel.destruction = displacedData.destruction;
    initialVoxel.velocityX = displacedData.velocityX;
    initialVoxel.velocityY = displacedData.velocityY;
    initialVoxel.lastDestructedAt = displacedData.lastDestructedAt;
    initialVoxel.tiredness = displacedData.tiredness;
    initialVoxel.modified = true;
    initialChunk.needsRenderRect.addPointByIndex(initialIndex);
  } else {
    // Normal move into empty space
    initialVoxel.resetVoxel(VOXEL_EMPTY, true);
    initialChunk.needsRenderRect.addPointByIndex(initialIndex);

    const finalVoxel = finalChunk.voxelAt(finalIndex);
    finalVoxel.uid = uid;
    finalVoxel.terrainType = terrainType;
    finalVoxel.destruction = destruction;
    finalVoxel.velocityX = velocityX;
    finalVoxel.velocityY = velocityY;
    finalVoxel.lastDestructedAt = lastDestructedAt;
    finalVoxel.tiredness = tiredness;
    finalVoxel.modified = true;
    finalChunk.needsRenderRect.addPointByIndex(finalIndex);
  }

  const finalVoxel = finalChunk.voxelAt(finalIndex);
  if (deltaY !== 0) {
    finalVoxel.tiredness = 0;
  } else {
    finalVoxel.tiredness = tiredness;
  }

  return MOVED_MOVERESULT;
}

// randomly spawn rain at the top of a full-air chunk
function spawnRain(chunkList: VoxelChunkPrepared[]) {
  const amount = 2;

  const minRainChunkY = GameEngine.voxelTerrain.worldHeightInChunks + 1;
  const maxRainChunkY = GameEngine.voxelTerrain.worldHeightInChunks + 2;

  for (const chunk of chunkList) {
    // if (!chunk.isAllAirOrLiquid()) continue;
    if (
      chunk.chunkPlacement.y < minRainChunkY ||
      chunk.chunkPlacement.y > maxRainChunkY
    )
      continue;

    const len = chunk.voxelStructs.count;
    for (let i = 0; i < amount; i++) {
      const randomIndex = Math.floor(Math.random() * len);
      const voxelStruct = chunk.voxelAt(randomIndex);
      if (voxelStruct.terrainType !== VOXEL_EMPTY) continue;

      voxelStruct.resetVoxel(TERRAIN_TYPES.water, true);
      chunk.needsRenderRect.addPointByIndex(randomIndex);
    }
  }
}

function isRainySeason(simulationTick: number) {
  // it rains continuously for 500 ticks, then is not raining for 4500 ticks, repeating.
  const rainDuration = 1000;
  const rainInterval = 5000;
  return simulationTick % (rainDuration + rainInterval) < rainDuration;
}

const MAX_LIQUID_HORIZONTAL_TICKS = 6;
export const MAX_SMOKE_STALLED_TICKS = 30;

export function runSandSimulation(simulationTick: number) {
  if (simulationTick % 5 !== 0) return;

  const chunksMap = GameEngine.voxelTerrain.chunks;
  const chunkList = Array.from(chunksMap.values()).filter(
    (chunk) => chunk.voxelStructs != null,
  ) as VoxelChunkPrepared[];

  const { chunkWidthInVoxels: chunkW, chunkHeightInVoxels: chunkH } =
    GameEngine.voxelTerrain;

  if (isRainySeason(simulationTick)) {
    spawnRain(chunkList);
  }

  let moveResult: number;

  // Process chunks from bottom to top
  for (const chunk of chunkList) {
    const voxelStruct = chunk.voxelStructs;

    // Process each layer from bottom to top
    for (let layer = 0; layer < chunkH; layer++) {
      const layerStart = layer * chunkW;
      const layerEnd = layerStart + chunkW;

      // const nextDirX = layer % 2 === 0 ? -1 : 1;

      // Process each voxel in the layer from right to left
      simLoop: for (let i = layerEnd - 1; i >= layerStart; i--) {
        voxelStruct.at(i);

        if (voxelStruct.modified) continue simLoop; // already processed this tick

        const terrainType = voxelStruct.terrainType;
        if (terrainType === VOXEL_EMPTY) continue simLoop; // nothing here

        // SCENARIO: move up
        switch (terrainType) {
          case TERRAIN_TYPES.smoke: {
            // 50% chance of 0, 25% chance of -1, 25% chance of 1
            const dirX = getRandomBinary() + getRandomBinary() - 1;

            moveResult = moveVoxel(chunk, chunksMap, i, dirX, 1);
            if (moveResult === MOVED_MOVERESULT) {
              // Reset tiredness when smoke successfully moves
              voxelStruct.tiredness = 0;
              continue simLoop;
            }

            // Reset voxelStruct to point back to current voxel since moveVoxel may have changed it
            voxelStruct.at(i);

            // If smoke couldn't move, increase decay (tiredness)
            voxelStruct.tiredness += 1;

            break;
          }
        }

        // SCENARIO: move down
        switch (terrainType) {
          case TERRAIN_TYPES.water:
          case TERRAIN_TYPES.sand:
          case TERRAIN_TYPES.dirt:
          case TERRAIN_TYPES.grass: {
            // case TERRAIN_TYPES.lava
            voxelStruct.velocityY += GRAVITY;

            // round down to integer
            const deltaY = Math.floor(voxelStruct.velocityY); // floor so it's never 0
            if (deltaY === 0) continue simLoop;

            // 1. try to move down
            moveResult = moveVoxel(chunk, chunksMap, i, 0, deltaY);
            if (moveResult === MOVED_MOVERESULT) continue simLoop;

            // Reset voxelStruct to point back to current voxel since moveVoxel may have changed it
            voxelStruct.at(i);

            // reset velocityY since we failed to move via gravity
            voxelStruct.velocityY = 0;
          }
        }

        // SCENARIO: move diagonal down
        switch (terrainType) {
          case TERRAIN_TYPES.sand:
          case TERRAIN_TYPES.water: {
            if (voxelStruct.velocityX === 0) {
              voxelStruct.velocityX = getRandomBinary() === 0 ? -1 : 1;
            }
            const deltaX = Math.round(voxelStruct.velocityX);

            // 2. try to move diagonal some-side
            moveResult = moveVoxel(chunk, chunksMap, i, deltaX, -1);
            if (moveResult === MOVED_MOVERESULT) continue simLoop;

            // 3. try to move diagonal other-side
            moveResult = moveVoxel(chunk, chunksMap, i, -deltaX, -1);
            if (moveResult === MOVED_MOVERESULT) continue simLoop;

            // Reset voxelStruct to point back to current voxel since moveVoxel may have changed it
            // voxelStruct.at(i);
          }
        }

        // SCENARIO: move horizontal
        switch (terrainType) {
          case TERRAIN_TYPES.smoke:
          case TERRAIN_TYPES.water: {
            // if we are here, it means no vertical or diagonal movement was possible.
            // Now we deal with horizontal movement and tiredness.

            if (
              terrainType === TERRAIN_TYPES.water &&
              voxelStruct.tiredness >= MAX_LIQUID_HORIZONTAL_TICKS
            ) {
              // if it's tired, don't do anything else.
              // this voxel will be "stuck" until something below or diagonal moves
              // and resets its tiredness.
              continue simLoop;
            }

            if (voxelStruct.velocityX === 0) {
              voxelStruct.velocityX = getRandomBinary() === 0 ? -1 : 1;
            }
            const deltaX = Math.round(voxelStruct.velocityX);

            // 4. try to move horizontal some-side
            moveResult = moveVoxel(chunk, chunksMap, i, deltaX, 0);
            if (moveResult === MOVED_MOVERESULT) continue simLoop; // Moved horizontally, done for this tick. Tiredness is not increased.

            // Failed to move in primary horizontal direction.
            // Try the other direction.

            // Reset voxelStruct to point back to current voxel since moveVoxel may have changed it
            voxelStruct.at(i);

            // REVERSE velocityX if we failed to move horizontally for some-side
            if (deltaX !== 0) {
              voxelStruct.velocityX = -voxelStruct.velocityX;
            }

            // 5. try to move horizontal other-side
            moveResult = moveVoxel(chunk, chunksMap, i, -deltaX, 0);
            if (moveResult === MOVED_MOVERESULT) continue simLoop; // Moved horizontally, done.

            // Reset voxelStruct to point back to current voxel since moveVoxel may have changed it
            voxelStruct.at(i);

            // If we are here, it means we failed to move horizontally in either direction.
            // So we increase tiredness (for water) or decay (for smoke).
            voxelStruct.tiredness += 1;

            if (
              terrainType === TERRAIN_TYPES.smoke &&
              voxelStruct.tiredness >= MAX_SMOKE_STALLED_TICKS
            ) {
              voxelStruct.resetVoxel(VOXEL_EMPTY, true);
              chunk.needsRenderRect.addPointByIndex(i);
            }
            continue simLoop;
          }
        }
      }
    }
  }
}
